import { useMemo, useState, useCallback, useEffect } from "react";
import { useAccount } from "wagmi";
import { useDispatch, useSelector } from "react-redux";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import useSanction from "@hooks/useSanction";
import { ETH, RSETH, WRSETH } from "@config/contractsConfig";
import { CONNECT_WALLET, STABLE_VAULT } from "@constants/analytics";
import {
  toFixedWithoutRounding,
  MAX_FRACTION_DIGITS,
  isNumber,
} from "@constants/common";
import { EVENTS } from "@constants/reduxEvents";
import useDappConfig from "@hooks/useDappConfig";
import useTokenBalance from "@hooks/useTokenBalance";
import useExchangeRate from "@hooks/useExchangeRate";
import useStableVaultAllowance from "@hooks/useStableVaultAllowance";
import useStableVaultXTokenAllowance from "@hooks/useStableVaultXTokenAllowance";
import useStableAssetExchangeRate from "@hooks/useStableAssetExchangeRate";
import useStableVaultDepositLimit from "@hooks/useStableVaultDepositLimit";
import { updateUserData } from "store";
import { isFloat, toStringPrecision, trackEvent, emitReduxEvent } from "utils";
import { STABLE_VAULT_STRINGS } from "./constants";
import useStableVaultData from "@hooks/useStableVaultData";

export const useSbUsdVault = () => {
  const {
    isLoadingAPYData,
    formattedApy,
    formattedTvl,
    isLoadingTotalStaked,
    formattedETHStaked,
  } = useStableVaultData();

  const { isSanctioned } = useSanction();

  const vaultHeaderData = useMemo(
    () => ({
      isLoadingAPYData,
      isLoadingTotalStaked,
      totalStaked:
        formattedTvl && formattedTvl !== "-" ? `$${formattedTvl}` : "-",
      totalStakedEth: formattedETHStaked,
      apyValue: formattedApy,
      apyLabel: STABLE_VAULT_STRINGS.HEADER.STATS.APY,
      totalStakedLabel: STABLE_VAULT_STRINGS.HEADER.STATS.TVL,
      title: STABLE_VAULT_STRINGS.HEADER.TITLE,
      logo: {
        src: "/assets/vaults/stableVault.svg",
        label: "sbUSD Logo",
      },
      auditedUrl:
        "https://kerneldao.gitbook.io/kernel/getting-started/gain/stable-gain#audits",
    }),
    [
      isLoadingAPYData,
      isLoadingTotalStaked,
      formattedTvl,
      formattedETHStaked,
      formattedApy,
    ]
  );

  const tabsData = useMemo(
    () => STABLE_VAULT_STRINGS.SECTIONS.DEPOSIT_WITHDRAW.TABS,
    []
  );

  const portfolioConfig = useMemo(
    () => ({
      tabs: STABLE_VAULT_STRINGS.SECTIONS.PORTFOLIO.TABS,
      points: STABLE_VAULT_STRINGS.SECTIONS.PORTFOLIO.POINTS,
      overviewData: STABLE_VAULT_STRINGS.SECTIONS.OVERVIEW.DATA,
      aprReports: STABLE_VAULT_STRINGS.SECTIONS.PORTFOLIO.APR_REPORTS,
    }),
    []
  );

  return {
    vaultHeaderData,
    tabsData,
    portfolioConfig,
    isSanctioned,
    constants: STABLE_VAULT_STRINGS,
  };
};

// Stable Vault specific deposit logic
export const useSbUsdDeposit = () => {
  const [amount, setAmount] = useState("");
  const [error, setError] = useState("");

  const { isConnected, address } = useAccount();
  const { isStableVaultSupportedChain, config, stableVaultConfig } =
    useDappConfig();
  const { erc20Allowance, minDepositAmount } = useStableVaultAllowance();
  const {
    tokensBalances,
    xTokenTokenBalance,
    xTokenStableVaultBalance,
    nativeTokenBalance,
  } = useTokenBalance();

  const { selectedERC20StableVault, referralId, stableFormResetNonce } =
    useSelector((state: any) => state.user);
  const dispatch = useDispatch();
  const { openConnectModal } = useConnectModal();

  const tokenBalance = useMemo(() => {
    if (selectedERC20StableVault === config.xtoken.symbol)
      return xTokenTokenBalance;
    else if (selectedERC20StableVault === ETH) return nativeTokenBalance;
    return tokensBalances[selectedERC20StableVault] || 0;
  }, [
    xTokenTokenBalance,
    tokensBalances,
    selectedERC20StableVault,
    nativeTokenBalance,
    config.xtoken.symbol,
  ]);

  const conversionDataRsEth = useExchangeRate("stablevault");
  const stableAssetExchangeRate = useStableAssetExchangeRate();

  const conversionsRsETH = useMemo(() => {
    const conversionToERC20 = conversionDataRsEth?.exchangeRate;
    return {
      toERC20: conversionToERC20 || 0,
      fromERC20: conversionToERC20 ? 1 / conversionToERC20 : 0,
    };
  }, [conversionDataRsEth?.exchangeRate]);

  const getsbUsdExchange = useCallback(() => {
    // For USDC or USDT, use direct exchange rate
    if (
      selectedERC20StableVault === "USDC" ||
      selectedERC20StableVault === "USDT"
    ) {
      return Number(stableAssetExchangeRate?.toERC20);
    }

    // For rsETH or wrsETH, use 1:1 rate
    if (
      selectedERC20StableVault === RSETH ||
      selectedERC20StableVault === WRSETH
    ) {
      return 1;
    }

    // For other tokens (like ETH), multiply by rsETH rate
    const rsETHRate = Number(conversionsRsETH?.toERC20);
    return Number(stableAssetExchangeRate?.toERC20) * rsETHRate;
  }, [
    stableAssetExchangeRate?.toERC20,
    conversionsRsETH?.toERC20,
    selectedERC20StableVault,
  ]);

  const exchangeRate = getsbUsdExchange();

  const { maxDeposit } = useStableVaultDepositLimit();

  // Calculate the display value: 1 sbUSD = X USDC (same as withdraw)
  const valueInUsdc = stableAssetExchangeRate?.toERC20
    ? toFixedWithoutRounding(
        Number(stableAssetExchangeRate.toERC20).toString(),
        4
      )
    : undefined;

  const checkInput = useCallback(
    (value = amount.trim()) => {
      if (!isConnected) return "";
      let error = "";
      if (!+value || Number.isNaN(+value)) {
        error =
          "This field can't be blank or zero. Please provide valid input.";
      } else if (Number(value) > tokenBalance) {
        error = `You can't deposit more than available balance in your wallet.`;
      } else if (Number(value) < minDepositAmount) {
        error = `Minimum deposit is ${minDepositAmount} ${selectedERC20StableVault}.`;
      } else if (Number(value) > maxDeposit) {
        error = `Maximum deposit is ${maxDeposit} ${selectedERC20StableVault}.`;
      }
      return error;
    },
    [
      isConnected,
      tokenBalance,
      selectedERC20StableVault,
      amount,
      minDepositAmount,
      maxDeposit,
    ]
  );

  useEffect(() => {
    if (amount !== "") {
      const error = checkInput(amount);
      setError(error);
    }
  }, [tokenBalance, address, amount, checkInput]);

  // Clear amount & error when Services reports a successful deposit
  useEffect(() => {
    if (stableFormResetNonce) {
      setAmount("");
      setError("");
    }
  }, [stableFormResetNonce]);

  const isApproved = useMemo(() => {
    if (selectedERC20StableVault === ETH) return true;
    return (
      erc20Allowance &&
      Number(erc20Allowance) &&
      Number(erc20Allowance) >= Number(amount)
    );
  }, [erc20Allowance, amount, selectedERC20StableVault]);

  const convertedAmount = useMemo(() => {
    if (amount && !isNaN(Number(amount))) {
      const value = Number(amount) / (exchangeRate > 0 ? exchangeRate : 1);
      const formatedValue =
        value && isFloat(value)
          ? toFixedWithoutRounding(
              toStringPrecision(value),
              MAX_FRACTION_DIGITS
            )
          : toStringPrecision(value);
      if (!Number.isNaN(+formatedValue)) {
        return formatedValue;
      } else {
        return "0";
      }
    } else {
      return "0";
    }
  }, [amount, exchangeRate]);

  const handleClick = useCallback(
    (inputAmount?: string) => {
      const amountToUse = inputAmount || amount;

      if (!isConnected) {
        openConnectModal?.();
        trackEvent(CONNECT_WALLET, {
          type: STABLE_VAULT.DEPOSIT_CTA,
        });
      } else if (!isStableVaultSupportedChain) {
        emitReduxEvent(EVENTS.SWITCH_NETWORK);
      } else {
        if (isApproved) {
          if (!amountToUse) {
            setError(
              "This field can't be blank or zero. Please provide valid input."
            );
            return;
          }

          emitReduxEvent(EVENTS.HANDLE_STABLE_VAULT_DEPOSIT, {
            amount: amountToUse,
            amountOut: convertedAmount,
            referralId,
          });

          setAmount("");
        } else {
          emitReduxEvent(EVENTS.HANDLE_STABLE_VAULT_APPROVE, {
            amount: amountToUse,
          });
        }
      }
    },
    [
      isConnected,
      isStableVaultSupportedChain,
      isApproved,
      amount,
      openConnectModal,
      referralId,
      convertedAmount,
    ]
  );

  const handleChange = (value: string) => {
    const val = value.trim().replace(/,/g, "");
    if (isNumber(val) || val === "") {
      if (val.indexOf(".") === -1) {
        setAmount(val);
      } else {
        if (val.length - (val.indexOf(".") + 1) <= MAX_FRACTION_DIGITS) {
          setAmount(val === "." ? "0." : val);
        }
      }
    }
  };

  const handleMaxClick = useCallback(() => {
    const maxAmount = Math.min(tokenBalance, maxDeposit);
    handleChange(toFixedWithoutRounding(maxAmount.toString(), 4));
  }, [tokenBalance, maxDeposit]);

  const handleTokenSelect = (key: string) => {
    dispatch(
      updateUserData({
        ["selectedERC20StableVault"]: key,
      })
    );
  };

  return {
    amount,
    setAmount,
    error,
    setError,
    tokenBalance,
    isConnected,
    isApproved,
    isStableVaultSupportedChain,
    convertedAmount,
    exchangeRate,
    selectedERC20Vault: selectedERC20StableVault,
    vaultConfig: stableVaultConfig,
    xTokenVaultBalance: xTokenStableVaultBalance,
    conversionsDataSbUsd: stableAssetExchangeRate,
    valueInUsdc,
    handleClick,
    handleChange,
    handleMaxClick,
    handleTokenSelect,
  };
};

// Stable Vault specific withdraw logic
export const useSbUsdWithdraw = () => {
  const [amount, setAmount] = useState("");
  const [error, setError] = useState("");

  const { isConnected, address } = useAccount();
  const { stableVaultConfig, isStableVaultSupportedChain } = useDappConfig();
  const { xTokenStableVaultBalance, tokensBalances } = useTokenBalance();
  const { xTokenAllowance } = useStableVaultXTokenAllowance();
  const { stableFormResetNonce, selectedOutputTokenStableVault } = useSelector(
    (s: any) => s.user
  );
  const dispatch = useDispatch();
  const { openConnectModal } = useConnectModal();

  const selectedOutputToken = selectedOutputTokenStableVault || "USDC";

  const tokenBalance = xTokenStableVaultBalance;

  const outputTokenBalance = useMemo(() => {
    return tokensBalances[selectedOutputToken] || 0;
  }, [tokensBalances, selectedOutputToken]);

  const isApproved = useMemo(() => {
    return Boolean(
      xTokenAllowance &&
        Number(xTokenAllowance) &&
        Number(xTokenAllowance) >= Number(amount)
    );
  }, [xTokenAllowance, amount]);

  const checkInput = useCallback(
    (value = amount.trim()) => {
      if (!isConnected) return "";
      let error = "";
      if (!+value || Number.isNaN(+value)) {
        error =
          "This field can't be blank or zero. Please provide valid input.";
      } else if (Number(value) > tokenBalance) {
        error = `You can't withdraw more than available balance in your wallet.`;
      }
      return error;
    },
    [isConnected, tokenBalance, amount]
  );

  useEffect(() => {
    if (amount !== "") {
      const error = checkInput(amount);
      setError(error);
    }
  }, [tokenBalance, address, amount, checkInput]);

  // Clear amount & error when Services reports a successful withdraw
  useEffect(() => {
    if (stableFormResetNonce) {
      setAmount("");
      setError("");
    }
  }, [stableFormResetNonce]);

  const stableAssetExchangeRate = useStableAssetExchangeRate();

  const exchangeRate = Number(stableAssetExchangeRate?.toERC20);

  // Calculate the display value: 1 sbUSD = X USDC/USDT/etc
  const valueInUsdc =
    exchangeRate > 0
      ? toFixedWithoutRounding(exchangeRate.toString(), 4)
      : undefined;

  const convertedAmount = useMemo(() => {
    if (amount && isNumber(amount)) {
      const value = Number(amount) * exchangeRate;
      const formatedValue =
        value && isFloat(value)
          ? toFixedWithoutRounding(
              toStringPrecision(value),
              MAX_FRACTION_DIGITS
            )
          : toStringPrecision(value);
      if (!Number.isNaN(+formatedValue)) {
        return formatedValue;
      } else {
        return "0";
      }
    } else {
      return "0";
    }
  }, [amount, exchangeRate]);

  const handleClick = useCallback(
    (inputAmount?: string) => {
      const amountToUse = inputAmount || amount;

      if (!isConnected) {
        openConnectModal?.();
        trackEvent(CONNECT_WALLET, {
          type: STABLE_VAULT.WITHDRAW_CTA,
        });
      } else if (!isStableVaultSupportedChain) {
        emitReduxEvent(EVENTS.SWITCH_NETWORK);
      } else {
        if (isApproved) {
          if (!amountToUse) {
            setError(
              "This field can't be blank or zero. Please provide valid input."
            );
            return;
          }
          emitReduxEvent(EVENTS.HANDLE_STABLE_VAULT_WITHDRAW, {
            amount: amountToUse,
            amountOut: convertedAmount,
            outputToken: selectedOutputToken,
          });
          setAmount("");
        } else {
          emitReduxEvent(EVENTS.HANDLE_STABLE_VAULT_XTOKEN_APPROVE, {
            amount: amountToUse,
            outputToken: selectedOutputToken,
          });
        }
      }
    },
    [
      isConnected,
      amount,
      openConnectModal,
      isApproved,
      selectedOutputToken,
      isStableVaultSupportedChain,
      convertedAmount,
    ]
  );

  const handleChange = (value: string) => {
    const val = value.trim().replace(/,/g, "");
    if (isNumber(val) || val === "") {
      if (val.indexOf(".") === -1) {
        setAmount(val);
      } else {
        if (val.length - (val.indexOf(".") + 1) <= MAX_FRACTION_DIGITS) {
          setAmount(val === "." ? "0." : val);
        }
      }
    }
  };

  const handleMaxClick = useCallback(() => {
    setAmount(toFixedWithoutRounding(tokenBalance.toString(), 4));
  }, [tokenBalance]);

  const setSelectedOutputToken = useCallback(
    (token: string) => {
      dispatch(
        updateUserData({
          selectedOutputTokenStableVault: token,
        })
      );
    },
    [dispatch]
  );

  return {
    amount,
    setAmount,
    error,
    setError,
    tokenBalance,
    isConnected,
    isApproved,
    isStableVaultSupportedChain,
    convertedAmount,
    exchangeRate,
    vaultConfig: stableVaultConfig,
    outputTokenBalance,
    conversionsDataSbUsd: stableAssetExchangeRate,
    valueInUsdc,
    handleClick,
    handleChange,
    handleMaxClick,
    checkInput,
    selectedOutputToken,
    setSelectedOutputToken,
  };
};
